import { useEffect, useState, useMemo } from 'react';
import { Link } from 'react-router-dom';
import {
  BarChart3, Users, Zap, Brain, Wallet, UsersRound,
  TrendingUp, Building2, ArrowUpRight, Sparkles,
} from 'lucide-react';
import { StatCard, Spinner, PlanBadge } from '@/components/shared/UIComponents';
import { usePlatformStore } from '@/lib/store';
import { fetchAllUsageMetrics } from '@/lib/api';
import { formatCompact } from '@/utils/helpers';
import type { UsageMetric, FeatureUsageEntry, TenantPlan } from '@/types';

// ── Category styling ──────────────────────────────────────────────────────────
const CATEGORY_CONFIG: Record<string, { label: string; color: string; bg: string; icon: React.ReactNode }> = {
  core:          { label: 'Core',            color: 'text-blue-600 dark:text-blue-400',   bg: 'bg-blue-500',    icon: <Zap size={14} /> },
  'smart features': { label: 'Smart Features', color: 'text-violet-600 dark:text-violet-400', bg: 'bg-violet-500', icon: <Brain size={14} /> },
  budget:        { label: 'Budget',          color: 'text-emerald-600 dark:text-emerald-400', bg: 'bg-emerald-500', icon: <Wallet size={14} /> },
  collaboration: { label: 'Collaboration',   color: 'text-amber-600 dark:text-amber-400', bg: 'bg-amber-500',   icon: <UsersRound size={14} /> },
};

type RangeKey = '7' | '30' | '90';

export function SuperAdminAnalyticsPage() {
  const { tenants, loadTenants } = usePlatformStore();
  const [allMetrics, setAllMetrics] = useState<UsageMetric[]>([]);
  const [loading, setLoading] = useState(true);
  const [range, setRange] = useState<RangeKey>('30');
  const [sortCol, setSortCol] = useState<string>('total_usage');
  const [sortAsc, setSortAsc] = useState(false);

  useEffect(() => { if (tenants.length === 0) loadTenants(); }, []);

  useEffect(() => {
    setLoading(true);
    fetchAllUsageMetrics(Number(range))
      .then(setAllMetrics)
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [range]);

  // ── Derive latest metric per tenant ─────────────────────────────────────
  const latestByTenant = useMemo(() => {
    const map = new Map<string, UsageMetric>();
    for (const m of allMetrics) {
      const existing = map.get(m.tenant_id);
      if (!existing || m.date > existing.date) map.set(m.tenant_id, m);
    }
    return map;
  }, [allMetrics]);

  // ── Platform-wide feature aggregation ───────────────────────────────────
  const platformFeatureUsage = useMemo(() => {
    const totals = new Map<string, {
      feature_key: string; feature_name: string; category: string;
      total_usage: number; unique_users: number; tenant_count: number;
    }>();

    for (const [, metric] of latestByTenant) {
      for (const f of (metric.feature_usage_data || [])) {
        const existing = totals.get(f.feature_key);
        if (existing) {
          existing.total_usage += f.total_usage;
          existing.unique_users += f.unique_users;
          if (f.total_usage > 0) existing.tenant_count += 1;
        } else {
          totals.set(f.feature_key, {
            feature_key: f.feature_key,
            feature_name: f.feature_name,
            category: f.category,
            total_usage: f.total_usage,
            unique_users: f.unique_users,
            tenant_count: f.total_usage > 0 ? 1 : 0,
          });
        }
      }
    }
    return [...totals.values()].sort((a, b) => b.total_usage - a.total_usage);
  }, [latestByTenant]);

  // ── Platform-wide KPIs ──────────────────────────────────────────────────
  const totalFeatureUses = platformFeatureUsage.reduce((s, f) => s + f.total_usage, 0);
  const featuresWithUsage = platformFeatureUsage.filter(f => f.total_usage > 0).length;
  const totalFeatures = platformFeatureUsage.length;
  const tenantsWithData = latestByTenant.size;
  const avgFeaturesPerTenant = tenantsWithData > 0
    ? Math.round(platformFeatureUsage.reduce((s, f) => s + f.tenant_count, 0) / tenantsWithData)
    : 0;
  const topFeature = platformFeatureUsage[0];
  const adoptionRate = totalFeatures > 0 ? Math.round((featuresWithUsage / totalFeatures) * 100) : 0;

  // ── Category totals ─────────────────────────────────────────────────────
  const categoryTotals = useMemo(() => {
    const totals: Record<string, { usage: number; features: number }> = {};
    platformFeatureUsage.forEach(f => {
      const cat = (f.category || 'general').toLowerCase();
      if (!totals[cat]) totals[cat] = { usage: 0, features: 0 };
      totals[cat].usage += f.total_usage;
      totals[cat].features += 1;
    });
    return totals;
  }, [platformFeatureUsage]);

  const totalCategoryUsage = Object.values(categoryTotals).reduce((s, c) => s + c.usage, 0);

  // ── Per-tenant breakdown rows ───────────────────────────────────────────
  const tenantRows = useMemo(() => {
    const rows: Array<{
      id: string; name: string; plan: TenantPlan | undefined;
      users: number; featuresUsed: number; topFeature: string;
      totalUsage: number; adoptionPct: number;
    }> = [];

    for (const t of tenants) {
      const metric = latestByTenant.get(t.id);
      const fud: FeatureUsageEntry[] = metric?.feature_usage_data || [];
      const used = fud.filter(f => f.total_usage > 0).length;
      const total = fud.length;
      const topF = fud.length ? [...fud].sort((a, b) => b.total_usage - a.total_usage)[0] : null;
      const tu = fud.reduce((s, f) => s + f.total_usage, 0);

      rows.push({
        id: t.id,
        name: t.name,
        plan: t.plan as TenantPlan | undefined,
        users: t.current_user_count,
        featuresUsed: used,
        topFeature: topF?.feature_name || '—',
        totalUsage: tu,
        adoptionPct: total > 0 ? Math.round((used / total) * 100) : 0,
      });
    }

    // Sort
    rows.sort((a, b) => {
      let va: string | number = 0, vb: string | number = 0;
      if (sortCol === 'name') { va = a.name.toLowerCase(); vb = b.name.toLowerCase(); }
      else if (sortCol === 'users') { va = a.users; vb = b.users; }
      else if (sortCol === 'features_used') { va = a.featuresUsed; vb = b.featuresUsed; }
      else if (sortCol === 'total_usage') { va = a.totalUsage; vb = b.totalUsage; }
      else if (sortCol === 'adoption') { va = a.adoptionPct; vb = b.adoptionPct; }
      if (va < vb) return sortAsc ? -1 : 1;
      if (va > vb) return sortAsc ? 1 : -1;
      return 0;
    });

    return rows;
  }, [tenants, latestByTenant, sortCol, sortAsc]);

  const handleSort = (col: string) => {
    if (sortCol === col) setSortAsc(!sortAsc);
    else { setSortCol(col); setSortAsc(false); }
  };

  const SortIcon = ({ col }: { col: string }) => (
    <span className="text-[10px] ml-0.5">{sortCol === col ? (sortAsc ? '▲' : '▼') : '⇅'}</span>
  );

  if (loading && allMetrics.length === 0) {
    return <div className="flex justify-center py-20"><Spinner size="lg" /></div>;
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-xl font-bold text-surface-900">Platform Analytics</h1>
          <p className="text-sm text-surface-500 mt-1">Feature adoption and usage across all tenants</p>
        </div>
        <div className="flex gap-1 bg-surface-100 rounded-lg p-0.5">
          {(['7', '30', '90'] as RangeKey[]).map(r => (
            <button key={r} onClick={() => setRange(r)}
              className={`px-3 py-1.5 text-xs font-medium rounded-md transition-colors ${range === r
                ? 'bg-surface-0 text-surface-900 shadow-sm'
                : 'text-surface-500 hover:text-surface-700'
              }`}>
              {r}d
            </button>
          ))}
        </div>
      </div>

      {/* ── KPI StatCards ──────────────────────────────────────────────── */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard label="Total Feature Uses" value={formatCompact(totalFeatureUses)}
          icon={<BarChart3 size={18} />} delay={0} />
        <StatCard label="Avg Features / Tenant" value={avgFeaturesPerTenant}
          icon={<Building2 size={18} />} delay={50} />
        <StatCard label="Most Popular Feature" value={topFeature?.feature_name || '—'}
          change={topFeature ? `${formatCompact(topFeature.total_usage)} uses` : ''}
          changeType="neutral" icon={<Sparkles size={18} />} delay={100} />
        <StatCard label="Feature Adoption" value={`${adoptionRate}%`}
          change={`${featuresWithUsage}/${totalFeatures} features`}
          changeType={adoptionRate > 50 ? 'positive' : 'neutral'}
          icon={<TrendingUp size={18} />} delay={150} />
      </div>

      {/* ── Feature Popularity + Category Breakdown ────────────────────── */}
      <div className="grid grid-cols-1 lg:grid-cols-5 gap-6">
        {/* Feature Popularity (wider) */}
        <div className="glass-panel p-5 lg:col-span-3">
          <h3 className="text-sm font-semibold text-surface-900 mb-4">Feature Popularity</h3>
          {platformFeatureUsage.length === 0 ? (
            <div className="h-40 flex items-center justify-center text-sm text-surface-500">
              No feature usage data yet. Run a sync to populate.
            </div>
          ) : (
            <div className="space-y-2 max-h-[500px] overflow-y-auto pr-1 scrollbar-thin">
              {platformFeatureUsage.map(f => {
                const maxUsage = platformFeatureUsage[0]?.total_usage || 1;
                const pct = Math.round((f.total_usage / maxUsage) * 100);
                const cfg = CATEGORY_CONFIG[(f.category || '').toLowerCase()];
                return (
                  <div key={f.feature_key} className="group">
                    <div className="flex items-center justify-between mb-1">
                      <div className="flex items-center gap-2">
                        <span className="text-sm text-surface-700">{f.feature_name}</span>
                        <span className={`text-[10px] px-1.5 py-0.5 rounded bg-surface-100 ${cfg?.color || 'text-surface-500'}`}>
                          {cfg?.label || f.category}
                        </span>
                      </div>
                      <div className="flex items-center gap-3 text-xs">
                        <span className="text-surface-400">{f.tenant_count} tenant{f.tenant_count !== 1 ? 's' : ''}</span>
                        <span className="text-surface-400">{f.unique_users} user{f.unique_users !== 1 ? 's' : ''}</span>
                        <span className="font-medium text-surface-900 w-14 text-right">{formatCompact(f.total_usage)}</span>
                      </div>
                    </div>
                    <div className="h-1.5 bg-surface-200 rounded-full overflow-hidden">
                      <div className={`h-full rounded-full ${cfg?.bg || 'bg-brand-500'}`}
                        style={{ width: `${pct}%`, opacity: f.total_usage === 0 ? 0.2 : 0.7 }} />
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Category Breakdown */}
        <div className="glass-panel p-5 lg:col-span-2">
          <h3 className="text-sm font-semibold text-surface-900 mb-4">Category Breakdown</h3>
          <div className="space-y-4">
            {Object.entries(CATEGORY_CONFIG).map(([catKey, cfg]) => {
              const ct = categoryTotals[catKey];
              const pct = totalCategoryUsage > 0 && ct ? Math.round((ct.usage / totalCategoryUsage) * 100) : 0;
              return (
                <div key={catKey}>
                  <div className="flex items-center justify-between mb-1.5">
                    <div className="flex items-center gap-2">
                      <span className={cfg.color}>{cfg.icon}</span>
                      <span className="text-sm font-medium text-surface-700">{cfg.label}</span>
                    </div>
                    <div className="flex items-center gap-2 text-xs">
                      <span className="text-surface-400">{ct?.features || 0} features</span>
                      <span className="font-semibold text-surface-900">{formatCompact(ct?.usage || 0)}</span>
                    </div>
                  </div>
                  <div className="h-2.5 bg-surface-200 rounded-full overflow-hidden">
                    <div className={`h-full rounded-full ${cfg.bg}`} style={{ width: `${pct}%` }} />
                  </div>
                  <div className="text-[10px] text-surface-400 mt-0.5 text-right">{pct}% of total</div>
                </div>
              );
            })}
          </div>

          <div className="mt-4 pt-3 border-t border-overlay/[0.04]">
            <div className="flex items-center justify-between text-xs">
              <span className="text-surface-500">Total interactions</span>
              <span className="font-bold text-surface-900">{totalFeatureUses.toLocaleString()}</span>
            </div>
            <div className="flex items-center justify-between text-xs mt-1">
              <span className="text-surface-500">Tenants with data</span>
              <span className="font-bold text-surface-900">{tenantsWithData}</span>
            </div>
          </div>
        </div>
      </div>

      {/* ── Per-Tenant Breakdown Table ──────────────────────────────────── */}
      <div className="glass-panel p-5">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-sm font-semibold text-surface-900">Per-Tenant Feature Usage</h3>
          <span className="text-xs text-surface-500">{tenantRows.length} tenants</span>
        </div>

        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-overlay/[0.06]">
                <Th col="name" label="Tenant" sortCol={sortCol} sortAsc={sortAsc} onSort={handleSort} />
                <th className="text-left py-2.5 px-3 text-xs font-medium text-surface-500">Plan</th>
                <Th col="users" label="Users" sortCol={sortCol} sortAsc={sortAsc} onSort={handleSort} align="right" />
                <Th col="features_used" label="Features Used" sortCol={sortCol} sortAsc={sortAsc} onSort={handleSort} align="right" />
                <th className="text-left py-2.5 px-3 text-xs font-medium text-surface-500">Top Feature</th>
                <Th col="total_usage" label="Total Usage" sortCol={sortCol} sortAsc={sortAsc} onSort={handleSort} align="right" />
                <Th col="adoption" label="Adoption" sortCol={sortCol} sortAsc={sortAsc} onSort={handleSort} align="right" />
                <th className="w-8" />
              </tr>
            </thead>
            <tbody>
              {tenantRows.map(row => (
                <tr key={row.id} className="border-b border-overlay/[0.04] hover:bg-overlay/[0.02] transition-colors">
                  <td className="py-2.5 px-3 font-medium text-surface-900">{row.name}</td>
                  <td className="py-2.5 px-3">
                    <PlanBadge plan={row.plan?.name || 'free'} label={row.plan?.display_name} />
                  </td>
                  <td className="py-2.5 px-3 text-right text-surface-700">{row.users}</td>
                  <td className="py-2.5 px-3 text-right text-surface-700">{row.featuresUsed}</td>
                  <td className="py-2.5 px-3 text-surface-600">{row.topFeature}</td>
                  <td className="py-2.5 px-3 text-right font-medium text-surface-900">{row.totalUsage.toLocaleString()}</td>
                  <td className="py-2.5 px-3 text-right">
                    <span className={`text-xs font-medium ${
                      row.adoptionPct >= 75 ? 'text-emerald-600 dark:text-emerald-400'
                        : row.adoptionPct >= 40 ? 'text-amber-600 dark:text-amber-400'
                          : 'text-surface-500'
                    }`}>{row.adoptionPct}%</span>
                  </td>
                  <td className="py-2.5 px-1">
                    <Link to={`/admin/tenants/${row.id}`}
                      className="text-surface-400 hover:text-brand-500 transition-colors">
                      <ArrowUpRight size={14} />
                    </Link>
                  </td>
                </tr>
              ))}
              {tenantRows.length === 0 && (
                <tr>
                  <td colSpan={8} className="py-8 text-center text-sm text-surface-500">
                    No tenants found
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

// ── Sortable table header ─────────────────────────────────────────────────────
function Th({ col, label, sortCol, sortAsc, onSort, align = 'left' }: {
  col: string; label: string; sortCol: string; sortAsc: boolean;
  onSort: (col: string) => void; align?: 'left' | 'right';
}) {
  return (
    <th className={`py-2.5 px-3 text-xs font-medium text-surface-500 cursor-pointer hover:text-surface-700 select-none ${
      align === 'right' ? 'text-right' : 'text-left'
    }`} onClick={() => onSort(col)}>
      {label}
      <span className="text-[10px] ml-0.5">{sortCol === col ? (sortAsc ? '▲' : '▼') : '⇅'}</span>
    </th>
  );
}
